import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import { SalesPanelProvider } from "./context/SalesPanel/SalesPanelContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import ErrorBoundary from "./components/ErrorBoundary";

import "./styles/animations.css";

const queryClient = new QueryClient();

const App = () => {
  // ✅ Handler global para suprimir erros 401 de imagens silenciosamente
  useEffect(() => {
    // Interceptar erros de console relacionados a imagens 401
    const originalError = console.error;
    console.error = (...args) => {
      // ✅ PRIORIDADE 1: Verificar se é erro de vídeo ANTES de processar a mensagem
      
      // Verificar se é o log do index.html com "🔴 ERRO CAPTURADO:"
      if (args[0] === '🔴 ERRO CAPTURADO:' && args[1] && typeof args[1] === 'object') {
        const errorObj = args[1];
        
        // Verificar se tem target VIDEO
        if (errorObj.target) {
          const targetTag = errorObj.target.tagName;
          const targetSrc = errorObj.target.src || '';
          
          if (targetTag === 'VIDEO' || 
              targetSrc.includes('.mp4') || 
              targetSrc.includes('.webm') || 
              targetSrc.includes('.ogg') ||
              targetSrc.includes('VID-') ||
              targetSrc.includes('/video') ||
              targetSrc.includes('video/')) {
            return; // Suprimir erro de vídeo
          }
        }
        
        // Se todas as propriedades são undefined mas há um target, pode ser erro de vídeo
        if (!errorObj.message && !errorObj.filename && !errorObj.lineno && 
            !errorObj.colno && !errorObj.error && errorObj.target) {
          // Erro sem informações - verificar se é vídeo
          if (errorObj.target.tagName === 'VIDEO') {
            return; // Suprimir erro de vídeo
          }
        }
      }
      
      // Verificar o primeiro argumento se for um objeto
      if (args[0] && typeof args[0] === 'object') {
        const firstArg = args[0];
        
        // Verificar se tem target com tagName VIDEO
        if (firstArg.target && firstArg.target.tagName === 'VIDEO') {
          return; // Suprimir erro de vídeo
        }
        
        // Verificar se o target tem src que indica vídeo
        if (firstArg.target && firstArg.target.src) {
          const src = firstArg.target.src || '';
          if (src.includes('.mp4') || src.includes('.webm') || src.includes('.ogg') || 
              src.includes('VID-') || src.includes('/video') || src.includes('video/')) {
            return; // Suprimir erro de vídeo
          }
        }
        
        // Verificar se o objeto tem propriedades que indicam vídeo
        if (firstArg.tagName === 'VIDEO' || 
            (firstArg.src && (firstArg.src.includes('.mp4') || firstArg.src.includes('VID-')))) {
          return; // Suprimir erro de vídeo
        }
      }
      
      // Verificar se é erro 401 de imagem
      const errorMessage = args.join(' ');
      
      // ✅ Filtrar erros de vídeo - não são erros críticos
      if (
        errorMessage.includes('VIDEO') || 
        errorMessage.includes('video') ||
        errorMessage.includes('Erro ao carregar vídeo') ||
        errorMessage.includes('Erro ao decodificar o vídeo') ||
        errorMessage.includes('mp4') ||
        errorMessage.includes('VID-')
      ) {
        // Suprimir silenciosamente - não logar no console
        return;
      }
      
      if (
        errorMessage.includes('401') && 
        (errorMessage.includes('Failed to load resource') || errorMessage.includes('Unauthorized')) &&
        (errorMessage.includes('.jpeg') || errorMessage.includes('.jpg') || errorMessage.includes('contacts') || errorMessage.includes('user'))
      ) {
        // Suprimir silenciosamente - não logar no console
        return;
      }
      // ✅ Suprimir avisos do Material-UI sobre Tabs com valor undefined (são apenas avisos, não erros críticos)
      if (
        errorMessage.includes('Material-UI') && 
        errorMessage.includes('Tabs') && 
        errorMessage.includes('undefined')
      ) {
        // Suprimir silenciosamente - não logar no console
        return;
      }
      // ✅ CORREÇÃO: Suprimir erro do ResizeObserver que causa problemas de renderização
      if (
        errorMessage.includes('ResizeObserver') && 
        errorMessage.includes('loop completed with undelivered notifications')
      ) {
        // Suprimir silenciosamente - este erro é conhecido e não afeta a funcionalidade
        return;
      }
      
      // Para outros erros, logar normalmente
      originalError.apply(console, args);
    };

    // Handler para erros de imagens
    const handleImageError = (event) => {
      const target = event.target;
      if (target && target.tagName === 'IMG') {
        // Substituir por imagem padrão sem gerar erro
        if (target.src && !target.src.includes('nopicture.png')) {
          target.onerror = null; // Prevenir loop infinito
          target.src = '/nopicture.png';
        }
      }
    };

    // ✅ Handler para erros de vídeos - prevenir que erros de vídeo sejam logados como erros críticos
    const handleVideoError = (event) => {
      const target = event.target;
      if (target && target.tagName === 'VIDEO') {
        // Não fazer nada aqui - deixar o componente específico tratar o erro
        // Apenas prevenir que o erro seja propagado para o handler global
        event.stopPropagation();
      }
    };

    // Adicionar handler onerror em todas as imagens e vídeos
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Adicionar handler em imagens existentes
            if (node.tagName === 'IMG') {
              node.addEventListener('error', handleImageError);
            }
            // ✅ Adicionar handler em vídeos existentes
            if (node.tagName === 'VIDEO') {
              node.addEventListener('error', handleVideoError);
            }
            // Adicionar handler em imagens dentro do nó
            const images = node.querySelectorAll && node.querySelectorAll('img');
            if (images) {
              images.forEach((img) => {
                img.addEventListener('error', handleImageError);
              });
            }
            // ✅ Adicionar handler em vídeos dentro do nó
            const videos = node.querySelectorAll && node.querySelectorAll('video');
            if (videos) {
              videos.forEach((video) => {
                video.addEventListener('error', handleVideoError);
              });
            }
          }
        });
      });
    });

    // Observar mudanças no DOM
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Adicionar handler em imagens já existentes
    document.querySelectorAll('img').forEach((img) => {
      img.addEventListener('error', handleImageError);
    });

    return () => {
      // Restaurar console.error original
      console.error = originalError;
      // Parar observador
      observer.disconnect();
      // Remover handlers
      document.querySelectorAll('img').forEach((img) => {
        img.removeEventListener('error', handleImageError);
      });
    };
  }, []);

  // ✅ CORREÇÃO: Handler global para erros não capturados, incluindo ResizeObserver
  useEffect(() => {
    const handleError = (event) => {
      const errorMessage = event.message || String(event.error || event);
      
      // Suprimir erro do ResizeObserver que causa problemas de renderização
      if (
        errorMessage.includes('ResizeObserver') && 
        errorMessage.includes('loop completed with undelivered notifications')
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const errorMessage = String(event.reason || '');
      
      // Suprimir erro do ResizeObserver em promises rejeitadas
      if (
        errorMessage.includes('ResizeObserver') && 
        errorMessage.includes('loop completed with undelivered notifications')
      ) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  const [locale, setLocale] = useState();
  // ✅ Tratamento seguro de localStorage com fallback
  const getLocalStorageItem = (key, defaultValue = "") => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;
      // Tentar parsear se for JSON
      try {
        return JSON.parse(item);
      } catch {
        // Se não for JSON, retornar como string
        return item || defaultValue;
      }
    } catch (error) {
      console.warn(`⚠️ Erro ao ler localStorage "${key}":`, error);
      return defaultValue;
    }
  };

  const primaryColorLightStorage =
    getLocalStorageItem("primaryColorLight") || "#065183";
  const primaryColorDarkStorage =
    getLocalStorageItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = getLocalStorageItem("appName") || "";
  const appLogoLightFile = getLocalStorageItem("appLogoLight");
  const appLogoDarkFile = getLocalStorageItem("appLogoDark");
  const appLogoFaviconFile = getLocalStorageItem("appLogoFavicon");
  const appLogoLightStorage =
    appLogoLightFile && String(appLogoLightFile).trim()
      ? getBackendUrl() + "/public/" + String(appLogoLightFile).trim()
      : defaultLogoLight;
  const appLogoDarkStorage =
    appLogoDarkFile && String(appLogoDarkFile).trim()
      ? getBackendUrl() + "/public/" + String(appLogoDarkFile).trim()
      : defaultLogoDark;
  const appLogoFaviconStorage =
    appLogoFaviconFile && String(appLogoFaviconFile).trim()
      ? getBackendUrl() + "/public/" + String(appLogoFaviconFile).trim()
      : defaultLogoFavicon;
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredThemeRaw = getLocalStorageItem("preferredTheme");
  const preferredThemeNormalized =
    typeof preferredThemeRaw === "string"
      ? preferredThemeRaw.trim().toLowerCase()
      : "";
  const preferredTheme =
    preferredThemeNormalized === "dark" || preferredThemeNormalized === "light"
      ? preferredThemeNormalized
      : prefersDarkMode
        ? "dark"
        : "light";
  const [mode, setMode] = useState(preferredTheme);
  const [primaryColorLight, setPrimaryColorLight] =
    useState(primaryColorLightStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(primaryColorDarkStorage);
  const [appLogoLight, setAppLogoLight] = useState(appLogoLightStorage);
  const [appLogoDark, setAppLogoDark] = useState(appLogoDarkStorage);
  const [appLogoFavicon, setAppLogoFavicon] = useState(appLogoFaviconStorage);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor:
                mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores do tema
              borderRadius: "4px", // Bordas arredondadas
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#2a2a2a",
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#E0E0E0" : "#404040",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#BDBDBD" : "#505050",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores dinâmicas
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${primaryColorDark}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${primaryColorDark}CC`,
              contrastText: "#ffffff",
            },
            textPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
            optionsBackground: mode === "light" ? "#fafafa" : "#333",
            fancyBackground: mode === "light" ? "#fafafa" : "#333",
            total: mode === "light" ? "#fff" : "#222",
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : "#333",
            barraSuperior: mode === "light" ? primaryColorLight : "#666", // Usa cor do tema
          },

          typography: {
            fontFamily: [
              'Inter',
              'Roboto',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            button: {
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: 8, // Bordas arredondadas mas não excessivas
          },
          overrides: {
            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: {
                maxWidth: 'none !important',
              },
              maxWidthMd: {
                maxWidth: 'none !important',
              },
              maxWidthSm: {
                maxWidth: 'none !important',
              },
              maxWidthXl: {
                maxWidth: 'none !important',
              },
              maxWidthXs: {
                maxWidth: 'none !important',
              },
            },
          
            // ✅ VOLTAR: Papers com largura total
            MuiPaper: {
              root: {
                backgroundImage: 'none',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
              },
              rounded: {
                borderRadius: 12,
              },
              elevation1: {
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              },
              elevation2: {
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              },
              elevation3: {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para menus
            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para popovers
            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
              }
            },

            // Inputs melhorados
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? "#ccc" : "#555",
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            // Tabs usando cor do tema
            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: mode === "light"
                    ? `${primaryColorLight}08`
                    : `${primaryColorDark}08`,
                },
                '&.Mui-selected': {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                }
              }
            },

            // Drawer sem bordas
            MuiDrawer: {
              paper: {
                border: 'none',
              }
            },

            // AppBar transparente
            MuiAppBar: {
              root: {
                boxShadow: 'none',
              }
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // ✅ Helper para tratar erros de settings públicas (ignorar 403 silenciosamente)
    const handleSettingError = (error, settingName) => {
      // Ignorar silenciosamente erros 403 (token não configurado ou inválido)
      if (error?.response?.status === 403) {
        return; // Silencioso - é esperado quando ENV_TOKEN não está configurado
      }
      // Logar apenas outros erros
      console.warn(`Erro ao ler setting pública "${settingName}":`, error?.message || error);
    };

    getPublicSetting("primaryColorLight")
      .then((color) => {
        if (color && String(color).trim()) setPrimaryColorLight(String(color).trim());
      })
      .catch((error) => {
        handleSettingError(error, "primaryColorLight");
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        if (color && String(color).trim()) setPrimaryColorDark(String(color).trim());
      })
      .catch((error) => {
        handleSettingError(error, "primaryColorDark");
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        if (file && String(file).trim())
          setAppLogoLight(getBackendUrl() + "/public/" + String(file).trim());
      })
      .catch((error) => {
        handleSettingError(error, "appLogoLight");
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        if (file && String(file).trim())
          setAppLogoDark(getBackendUrl() + "/public/" + String(file).trim());
      })
      .catch((error) => {
        handleSettingError(error, "appLogoDark");
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        if (file && String(file).trim())
          setAppLogoFavicon(getBackendUrl() + "/public/" + String(file).trim());
      })
      .catch((error) => {
        handleSettingError(error, "appLogoFavicon");
      });
    getPublicSetting("appName")
      .then((name) => {
        if (name != null && String(name).trim()) setAppName(String(name).trim());
      })
      .catch((error) => {
        handleSettingError(error, "appName");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <ErrorBoundary>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <SalesPanelProvider>
              <Routes />
            </SalesPanelProvider>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </ErrorBoundary>
  );
};

export default App;
